// Revision mode in Write (the Mac's RevisionViews, LibraryModel+Revisions and EditorController's revision marks;
// review B17): the marks in the editor (`@writing-suite/editor`'s revisions: added words in their revision's colour, a
// caret mark at deletions, a change bar beside the text column), set from `RevisionDiff` 450 ms after typing stops and
// shifted by the editor on every edit meanwhile; the card for the change at the caret (old struck through → new,
// previous/next, Reject as an undoable edit through the editor, Accept as a base patch); the footer's revision menu;
// and the sheets: Start Revision (a name, measured from now or an earlier checkpoint) and the confirmations for Accept
// All (the marks go, revision mode ends, "Accepted revisions (…)") and Reject All ("Before rejecting revisions", every
// changed document back to its base, open ones through the editor so Undo brings the text back).
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore, type RefObject } from 'react';
import { revisionChangesByAuthor, UNKNOWN_DEVICE, UNKNOWN_COLOR, type Members } from '@writing-suite/core/members';
import { byAuthorSnapshot, hueIndex, membersOf, revisionsByAuthor, setRevisionsByAuthor, subscribeByAuthor } from '../../store/members.ts';
import { PersonDot } from '../People.tsx';
import type { OpenedProject, Project } from '@writing-suite/core';
import {
  changeAt, changeID, revisionChanges, revisionTexts, rejectReplacement, type RevisionChange, type RevisionTexts,
} from '@writing-suite/core/revise';
import { announce } from '../../shell/context.ts';
import type { LiveProject } from '../../store/project.ts';
import {
  acceptAllRevisions, acceptChange, rejectAllRevisions, revisionsOf, setShowsRevisionMarks, showsRevisionMarks, startRevision, suggestedRevisionName,
  useRevisePrefs,
} from '../../store/revise.ts';
import { setRevisionControls } from '../../store/reviseEditor.ts';
import { ConfirmDialog, type MenuEntry, MenuButton } from '../Menu.tsx';
import type { ScriveningsApi } from '../Scrivenings.tsx';

/** How long after the last keystroke the exact marks are worked out (`EditorController.edited`). */
export const REVISION_PAUSE_MS = 450;

export type RevisionSheet = 'start' | 'acceptAll' | 'rejectAll';

export interface CaretChange { fragmentId: string; change: RevisionChange }

export interface RevisionMode {
  /** Revision mode is on for the project. */
  active: boolean;
  /** …and the marks are shown on this device. */
  shown: boolean;
  /** The current revision's index (new typing's colour). */
  level: number;
  names: string[];
  /** Changes in the open text. */
  count: number;
  caretChange: CaretChange | null;
  onCaret(c: { fragmentId: string; offset: number } | null): void;
  onTyped(): void;
  /** Moves to the next (or previous) change, selecting it. */
  reveal(forward: boolean): boolean;
  accept(): void;
  reject(): void;
  accepting: boolean;
  /** Revision marks by author (W9c, shared projects): `available` when the project has members; `on` when the marks
   *  are coloured by who wrote the words, each level then a writer (`names`, `colors`: "r g b"). */
  byAuthor: { available: boolean; on: boolean; names: string[]; colors: string[] };
  /** Who wrote a change's added words (by author only). */
  authorsAt?(fragmentId: string, change: RevisionChange): { name: string; color: string }[];
}

/** The writers as revision levels: the members in order, then an unknown device. */
function authorLevels(members: Members | null) {
  const list = members?.list ?? [];
  return {
    names: [...list.map((m) => m.name), UNKNOWN_DEVICE],
    colors: [...list.map((m) => m.color || UNKNOWN_COLOR), UNKNOWN_COLOR],
    hues: [...list.map((m, i) => hueIndex(m.color, i)), 12],
  };
}

/** A key for what a fragment's base depends on: the revisions' checkpoints and its accepted patches. */
function baseKey(project: OpenedProject, id: string): string {
  const r = revisionsOf(project);
  return JSON.stringify([r.revisions.map((x) => x.checkpointID), r.accepted[id] ?? []]);
}

export function useRevisionMode({ project, live, ids, api }: { project: Project; live: LiveProject | null; ids: string[]; api: RefObject<ScriveningsApi | null> }): RevisionMode {
  useRevisePrefs();
  const opened = project as OpenedProject;
  const revisions = revisionsOf(project);
  const active = revisions.revisions.length > 0;
  const shown = active && showsRevisionMarks();
  useSyncExternalStore(subscribeByAuthor, byAuthorSnapshot);
  const members = membersOf(live);
  const byAuthor = !!members && revisionsByAuthor();
  // By author, new typing takes this account's colour (its index among the members).
  const ownIndex = members && live?.memberId ? members.list.findIndex((m) => m.id === live.memberId) : -1;
  const level = byAuthor ? (ownIndex >= 0 ? ownIndex : (members?.list.length ?? 0)) : Math.max(0, revisions.revisions.length - 1);
  const authors = useMemo(() => authorLevels(members), [members]);
  /** Each shown fragment's base texts (null: its history at a revision's checkpoint hasn't arrived). */
  const texts = useRef(new Map<string, { key: string; texts: RevisionTexts | null }>());
  /** The text each fragment's changes were worked out from. */
  const computed = useRef(new Map<string, string>());
  const [changes, setChanges] = useState<Map<string, RevisionChange[]>>(new Map());
  const changesRef = useRef(changes);
  changesRef.current = changes;
  const [caretChange, setCaretChange] = useState<CaretChange | null>(null);
  const lastCaret = useRef<{ fragmentId: string; offset: number } | null>(null);
  const [accepting, setAccepting] = useState(false);
  const idsKey = ids.join('\n');
  const state = useRef({ opened, live, ids, shown, level, byAuthor, members, hues: authors.hues });
  state.current = { opened, live, ids, shown, level, byAuthor, members, hues: authors.hues };

  const editorText = (id: string) => api.current?.text(id) ?? null;

  const textsFor = useCallback((id: string): RevisionTexts | null => {
    const { opened, live } = state.current;
    if (!live) return null;
    const key = baseKey(opened, id);
    const hit = texts.current.get(id);
    if (hit && hit.key === key && hit.texts) return hit.texts;
    const found = revisionTexts(live.documents(), id);
    texts.current.set(id, { key, texts: found });
    return found;
  }, []);

  const caretIn = useCallback((map: Map<string, RevisionChange[]>, c: { fragmentId: string; offset: number } | null): CaretChange | null => {
    if (!c) return null;
    const found = changeAt(map.get(c.fragmentId) ?? [], c.offset);
    return found ? { fragmentId: c.fragmentId, change: found } : null;
  }, []);

  /** Shows these changes as the editor's marks, and the card for the caret. */
  const show = useCallback((map: Map<string, RevisionChange[]>) => {
    const { ids, level, byAuthor, members, live, hues } = state.current;
    setChanges(map);
    // By author: the same changes, each added word coloured by whoever wrote it (read from the document, which holds
    // the editor's text; a fragment whose text differs for a moment keeps its marks by revision until the next pause).
    const coloured = (id: string) => {
      const changes = map.get(id) ?? [];
      const doc = live?.doc(`fragment.${id}`);
      if (!byAuthor || !members || !doc || live!.text(id) !== editorText(id)) return changes;
      return revisionChangesByAuthor(changes, doc, members);
    };
    api.current?.handle()?.setRevisionMarks({
      fragments: ids.map((id) => ({ fragmentId: id, changes: coloured(id) })), level, ...(byAuthor ? { hues } : {}),
    });
    setCaretChange((old) => {
      const next = caretIn(map, lastCaret.current);
      return old && next && old.fragmentId === next.fragmentId && changeID(old.change) === changeID(next.change) && old.change.inserted === next.change.inserted ? old : next;
    });
  }, [api, caretIn]);

  /** Diffs the fragments whose text changed since (all of them with `force`), a few at a time so a book's worth
   *  never holds the page up, then shows the marks. */
  const generation = useRef(0);
  const recompute = useCallback((force: boolean) => {
    const token = ++generation.current;
    const { ids, shown } = state.current;
    if (!shown) return;
    const map = new Map(changesRef.current);
    for (const id of [...map.keys()]) if (!ids.includes(id)) map.delete(id);
    const work = ids.filter((id) => force || computed.current.get(id) !== editorText(id));
    if (!work.length) {
      if (force) show(map);
      return;
    }
    let k = 0;
    const step = () => {
      if (token !== generation.current) return;
      const t0 = performance.now();
      while (k < work.length && performance.now() - t0 < 12) {
        const id = work[k++];
        const t = textsFor(id);
        const now = editorText(id);
        if (!t || now === null) {
          map.delete(id);
          continue;
        }
        map.set(id, revisionChanges(t.base, now, t.boundaries));
        computed.current.set(id, now);
      }
      if (k < work.length) setTimeout(step, 0);
      else show(map);
    };
    step();
  }, [show, textsFor]); // eslint-disable-line react-hooks/exhaustive-deps

  // On, off, another selection or another revision: everything again.
  const revisionsKey = JSON.stringify(revisions.revisions.map((r) => r.checkpointID));
  useEffect(() => {
    const h = api.current?.handle();
    if (!shown) {
      generation.current++;
      h?.setRevisionMarks(null);
      setChanges(new Map());
      setCaretChange(null);
      computed.current.clear();
      return;
    }
    computed.current.clear();
    recompute(true);
  }, [shown, idsKey, revisionsKey, level, byAuthor]); // eslint-disable-line react-hooks/exhaustive-deps

  // Typing (and other devices' changes): the exact marks 450 ms after it stops; the editor shifts them meanwhile.
  const pause = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onTyped = useCallback(() => {
    if (!state.current.shown) return;
    if (pause.current) clearTimeout(pause.current);
    pause.current = setTimeout(() => recompute(false), REVISION_PAUSE_MS);
  }, [recompute]);
  useEffect(() => {
    // A rebuilt project (a remote change, an accepted patch): texts whose base moved are read again at the pause.
    onTyped();
  }, [project]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => {
    if (pause.current) clearTimeout(pause.current);
    generation.current++;
  }, []);

  const onCaret = useCallback((c: { fragmentId: string; offset: number } | null) => {
    lastCaret.current = c;
    if (!state.current.shown) return;
    const next = caretIn(changesRef.current, c);
    setCaretChange((old) => (old && next && old.fragmentId === next.fragmentId && changeID(old.change) === changeID(next.change) ? old : next));
  }, [caretIn]);

  const reveal = useCallback((forward: boolean): boolean => {
    const h = api.current?.handle();
    const { ids } = state.current;
    if (!h) return false;
    const caret = h.caret();
    const sel = h.selection();
    const ci = caret ? ids.indexOf(caret.fragmentId) : -1;
    const at = caret?.offset ?? 0;
    const collapsed = !sel || sel.from === sel.to;
    const all = ids.flatMap((id, fi) => (changesRef.current.get(id) ?? []).map((c) => ({ id, fi, c })));
    const current = caretChange ? changeID(caretChange.change) : null;
    const target = forward
      ? all.find(({ fi, c }) => fi > ci || (fi === ci && (c.current[0] > at || (c.current[0] === at && collapsed && c.current[1] > c.current[0] && changeID(c) !== current))))
      : [...all].reverse().find(({ fi, c }) => fi < ci || (fi === ci && c.current[0] < (sel && sel.fragmentId === ids[ci] ? sel.from : at)));
    if (!target) return false;
    api.current?.reveal(target.id, target.c.current[0], target.c.current[1]);
    lastCaret.current = { fragmentId: target.id, offset: target.c.current[0] };
    setCaretChange({ fragmentId: target.id, change: target.c });
    return true;
  }, [api, caretChange]);

  const accept = useCallback(() => {
    const live = state.current.live;
    if (!caretChange || !live || accepting) return;
    setAccepting(true);
    try {
      const current = editorText(caretChange.fragmentId) ?? live.text(caretChange.fragmentId);
      if (!acceptChange(live, caretChange.change, caretChange.fragmentId, current)) {
        announce('That change no longer fits; it will be marked again at the next pause.');
        return;
      }
      // The base moved on: this fragment's texts again, its marks at once, then the next change.
      texts.current.delete(caretChange.fragmentId);
      computed.current.delete(caretChange.fragmentId);
      const t = textsFor(caretChange.fragmentId);
      const map = new Map(changesRef.current);
      if (t) {
        map.set(caretChange.fragmentId, revisionChanges(t.base, current, t.boundaries));
        computed.current.set(caretChange.fragmentId, current);
      }
      changesRef.current = map;
      show(map);
      announce('Accepted.');
      reveal(true);
    } finally {
      setAccepting(false);
    }
  }, [caretChange, accepting, show, textsFor, reveal]); // eslint-disable-line react-hooks/exhaustive-deps

  const reject = useCallback(() => {
    if (!caretChange) return;
    const t = textsFor(caretChange.fragmentId);
    if (!t) return;
    const r = rejectReplacement(caretChange.change, t.base);
    if (api.current?.replaceExact(caretChange.fragmentId, r.from, r.to, r.text)) {
      announce('Rejected: what was there before is back. Undo brings the change back.');
      recompute(false);
    }
  }, [caretChange, api, textsFor, recompute]);

  const count = useMemo(() => [...changes.values()].reduce((n, l) => n + l.length, 0), [changes]);
  // The Project menu's Next and Previous Change reach here.
  useEffect(() => {
    if (!shown) {
      setRevisionControls(null);
      return;
    }
    setRevisionControls({ changeCount: count, reveal });
    return () => setRevisionControls(null);
  }, [shown, count, reveal]);

  return {
    active, shown, level, names: revisions.revisions.map((r) => r.name), count, caretChange: shown ? caretChange : null, onCaret, onTyped, reveal, accept, reject, accepting,
    byAuthor: { available: !!members, on: byAuthor, names: authors.names, colors: authors.colors },
    authorsAt: (fragmentId, change) => {
      const doc = live?.doc(`fragment.${fragmentId}`);
      if (!members || !doc || live!.text(fragmentId) !== editorText(fragmentId)) return [];
      const levels = [...new Set(revisionChangesByAuthor([change], doc, members)[0].levels.map((l) => l.level))];
      return levels.map((l) => ({ name: authors.names[l] ?? UNKNOWN_DEVICE, color: authors.colors[l] ?? UNKNOWN_COLOR }));
    },
  };
}

// MARK: Views

const clip = (s: string) => {
  const flat = s.replace(/\n/g, ' ¶ ').trim();
  return [...flat].length > 90 ? [...flat].slice(0, 88).join('') + '…' : flat;
};

/** The change the caret is in, over the foot of the page. */
export function RevisionCard({ mode, canWrite }: { mode: RevisionMode; canWrite: boolean }) {
  const c = mode.caretChange;
  if (!c) return null;
  const change = c.change;
  const level = change.levels[0]?.level ?? change.deletionLevel;
  const name = mode.names[level] ?? 'Revision';
  // By author, the card names the writers of the change's words (the levels here are revisions; the marks show who).
  const writers = mode.byAuthor.on ? mode.authorsAt?.(c.fragmentId, change) ?? [] : [];
  const deleted = clip(change.deleted), inserted = clip(change.inserted);
  return (
    <section className="revision-card" aria-label={`Change in ${name}`} data-change={changeID(change)} data-fragment-id={c.fragmentId}>
      <span className={`revision-dot rev-${level % 5}`} aria-hidden="true" />
      <div className="revision-card-text">
        <p className="revision-card-name">
          {name}
          {writers.length ? (
            <span className="revision-card-authors" data-change-authors={writers.map((w) => w.name).join(', ')}>
              {' · '}
              {writers.map((w, i) => (
                <span key={w.name}>
                  {i ? ', ' : ''}
                  <PersonDot color={w.color} />
                  {w.name}
                </span>
              ))}
            </span>
          ) : null}
        </p>
        <p className="revision-card-summary">
          {deleted && inserted ? (
            <>
              <del>{deleted}</del>
              <span className="revision-arrow" aria-label="became">  →  </span>
              <ins>{inserted}</ins>
            </>
          ) : deleted ? (
            <del>{deleted}</del>
          ) : inserted ? (
            <ins>{inserted}</ins>
          ) : (
            <span className="revision-spacing">A change in spacing</span>
          )}
        </p>
      </div>
      <div className="revision-card-actions">
        <button type="button" className="pill outline revision-step" onMouseDown={(e) => e.preventDefault()} onClick={() => mode.reveal(false)} aria-label="Previous Change" title="Previous change (⌥⌘[)">
          ‹
        </button>
        <button type="button" className="pill outline revision-step" onMouseDown={(e) => e.preventDefault()} onClick={() => mode.reveal(true)} aria-label="Next Change" title="Next change (⌥⌘])">
          ›
        </button>
        <button type="button" className="pill outline" onMouseDown={(e) => e.preventDefault()} onClick={mode.reject} disabled={!canWrite} title="Put back what was there before">
          Reject
        </button>
        <button type="button" className="pill" onMouseDown={(e) => e.preventDefault()} onClick={mode.accept} disabled={!canWrite || mode.accepting} title="Keep the change and stop marking it">
          Accept
        </button>
      </div>
    </section>
  );
}

/** Everything revision mode offers, as menu entries (the footer's menu and the Project menu). */
export function revisionEntries(o: { active: boolean; shown: boolean; count: number; canWrite: boolean; reveal: ((forward: boolean) => boolean) | null; open(sheet: RevisionSheet): void;
  /** A shared project's marks can be coloured by who wrote the words (W9c). */
  byAuthor?: { available: boolean; on: boolean } }): MenuEntry[] {
  if (!o.active) return [{ label: 'Start Revision…', disabled: !o.canWrite, onSelect: () => o.open('start') }];
  const moving = !!o.reveal && o.shown && o.count > 0;
  return [
    { kind: 'check', label: 'Show Revision Marks', checked: o.shown, onSelect: () => setShowsRevisionMarks(!o.shown) },
    ...(o.byAuthor?.available
      ? [{ kind: 'check' as const, label: 'Colour by Author', checked: o.byAuthor.on, disabled: !o.shown, onSelect: () => setRevisionsByAuthor(!o.byAuthor!.on) }]
      : []),
    { label: 'Next Change', shortcut: '⌥⌘]', disabled: !moving, onSelect: () => void o.reveal?.(true) },
    { label: 'Previous Change', shortcut: '⌥⌘[', disabled: !moving, onSelect: () => void o.reveal?.(false) },
    { kind: 'separator' },
    { label: 'Start New Revision…', disabled: !o.canWrite, onSelect: () => o.open('start') },
    { label: 'Accept All Changes…', disabled: !o.canWrite, onSelect: () => o.open('acceptAll') },
    { label: 'Reject All Changes…', disabled: !o.canWrite, onSelect: () => o.open('rejectAll') },
  ];
}

/** Write's footer: the current revision and the open text's changes, with everything revision mode does. */
export function RevisionMenu({ mode, canWrite, open }: { mode: RevisionMode; canWrite: boolean; open(sheet: RevisionSheet): void }) {
  if (!mode.active) return null;
  const current = mode.names[mode.names.length - 1] ?? 'Revision';
  return (
    <MenuButton
      label={`Revision: ${current}, ${mode.shown ? `${mode.count} ${mode.count === 1 ? 'change' : 'changes'}` : 'marks hidden'}`}
      className="foot-button revision-menu"
      title="Revision mode"
      entries={() => revisionEntries({ active: true, shown: mode.shown, count: mode.count, canWrite, reveal: mode.reveal, open, byAuthor: mode.byAuthor })}
    >
      {mode.byAuthor.on ? <PersonDot color={mode.byAuthor.colors[mode.level] ?? UNKNOWN_COLOR} className="revision-by-author" /> : <span className={`revision-dot rev-${mode.level % 5}`} aria-hidden="true" />}
      <span className="revision-name" data-revision-by={mode.byAuthor.on ? 'author' : 'revision'}>{mode.byAuthor.on ? `${current}, by author` : current}</span>
      <span className="revision-count" data-revision-changes={mode.shown ? mode.count : ''}>
        {mode.shown ? `${mode.count} ${mode.count === 1 ? 'change' : 'changes'}` : 'marks hidden'}
      </span>
    </MenuButton>
  );
}

/** Start Revision: a name, and the text to measure changes from (now, or an earlier checkpoint). */
export function StartRevisionSheet({ project, live, onClose, onStarted }: { project: Project; live: LiveProject | null; onClose(): void; onStarted?(): void }) {
  const opened = project as OpenedProject;
  const revisions = revisionsOf(project);
  const suggested = suggestedRevisionName(project);
  const [name, setName] = useState('');
  const [from, setFrom] = useState('');
  const [error, setError] = useState<string | null>(null);
  const checkpoints = useMemo(() => [...(opened.record?.checkpoints ?? [])].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()).slice(0, 40), [opened]);
  const when = new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
  const title = revisions.revisions.length ? 'Start New Revision' : 'Start Revision';
  const start = async () => {
    if (!live) return;
    try {
      const chosen = name.trim() || suggested;
      await startRevision(live, chosen, from || null);
      announce(`Revision “${chosen}” started. Changes from now on are marked.`);
      onClose();
      onStarted?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  return (
    <dialog
      className="confirm start-revision"
      aria-labelledby="start-revision-title"
      ref={(d) => {
        if (d && !d.open) d.showModal();
      }}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
    >
      <form
        method="dialog"
        onSubmit={(e) => {
          e.preventDefault();
          void start();
        }}
      >
        <h2 id="start-revision-title">{title}</h2>
        <label className="field-label stacked-label" htmlFor="revision-name">
          Name
        </label>
        <input id="revision-name" type="text" value={name} placeholder={suggested} autoFocus onChange={(e) => setName(e.target.value)} />
        <label className="field-label stacked-label" htmlFor="revision-from">
          Mark changes since
        </label>
        <select id="revision-from" value={from} onChange={(e) => setFrom(e.target.value)}>
          <option value="">Now</option>
          {checkpoints.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name || 'Untitled'} · {when.format(c.createdAt)}
            </option>
          ))}
        </select>
        <p className="confirm-message small">
          {revisions.revisions.length
            ? `Words you add from now on show in this revision’s colour. Changes since “${revisions.revisions[0].name}” stay marked until you accept or reject them.`
            : 'A checkpoint records the text as it stands. From then on, words you add are coloured, deletions are marked, and a bar in the margin shows each changed line. Accept or reject each change, or all of them at once.'}
        </p>
        {error ? <p className="error" role="alert">{error}</p> : null}
        <div className="actions">
          <button type="button" className="button secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="button primary" disabled={!live?.writable}>
            Start
          </button>
        </div>
      </form>
    </dialog>
  );
}

/** Accept All / Reject All, confirmed. */
export function RevisionConfirm({ action, project, live, onClose }: { action: 'acceptAll' | 'rejectAll'; project: Project; live: LiveProject | null; onClose(): void }) {
  return action === 'acceptAll' ? (
    <ConfirmDialog
      title="Accept every change?"
      message={<p>The text stays as it is and the marks go. A checkpoint records the moment, so History can still show what changed.</p>}
      confirm="Accept All and Finish"
      onCancel={onClose}
      onConfirm={() => {
        onClose();
        if (!live) return;
        void acceptAllRevisions(live).then(() => announce('Every change accepted; revision mode has ended.'));
      }}
    />
  ) : (
    <ConfirmDialog
      title="Reject every change?"
      message={<p>Every changed document goes back to how it was when the revision began. A checkpoint is recorded first, so you can restore today’s text from History. Documents open in Write are put back there, so Undo brings your text back too.</p>}
      confirm="Reject All"
      onCancel={onClose}
      onConfirm={() => {
        onClose();
        if (!live) return;
        void rejectAllRevisions(live, project as OpenedProject).then(({ changed, refused }) => {
          if (refused) announce(`${refused} open ${refused === 1 ? 'document' : 'documents'} couldn’t be put back in the editor. Use Reject on each change instead.`);
          else announce(changed ? `${changed} ${changed === 1 ? 'document' : 'documents'} put back.` : 'Nothing to reject.');
        });
      }}
    />
  );
}

/** The sheets a revision command opens, over whatever page asked. */
export function RevisionSheets({ sheet, project, live, onClose }: { sheet: RevisionSheet | null; project: Project; live: LiveProject | null; onClose(): void }) {
  if (!sheet) return null;
  if (sheet === 'start') return <StartRevisionSheet project={project} live={live} onClose={onClose} />;
  return <RevisionConfirm action={sheet} project={project} live={live} onClose={onClose} />;
}
